import React, { useState, useEffect, useRef, useCallback } from "react";
import { matchPath, Redirect, Route, Router, Switch } from "react-router-dom";
import "./App.scss";
import "../styles/sharedStyles.scss";
import PreviewTermsConditions from "../shared/previewTermsConditions/PreviewTermsConditions";
import TermsDeclined from "../components/declineTerms/TermsDeclined";
import Dealer from "./pure/Dealer";
import Help from "../components/help/help";
import CustomerReservations from "../components/customerReservation/CustomerReservations";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import Footer from "../shared/Footer/Footer";
import QuickStart from "../components/quickStart/QuickStart";
import ProgressBarTabs from "../components/progressBar/ProgressBarTabs";
import AccessDeniedAsBevDisabled from "../components/accessDenied/AccessDeniedAsBevDisabled";
import AccessDeniedAsNotOnBoarded from "../components/accessDenied/AccessDeniedAsNotOnBoarded";
import InvalidRoute from "../components/accessDenied/InvalidRoute";
import BusinessSettings from "../components/businessSetting/BusinessSettings";
import DealerDashboard from "../components/dashboard/dealerDashBoard/DealerDashboard";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import PricingWrapper from "../components/pricing/pricingWrapper/PricingWrapper";
import DepositPricing from "../components/pricing/pricingWrapper/depositPricing/DepositPricing";
import HasPermissionTo from "../common/HasPermissionTo";
import DealerOnboardingClient from "../shared/clients/DealerOnboardingClient";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import DealerGroupingClient from "../shared/clients/DealerGroupingClient";
import PendingActionClient from "../shared/clients/PendingActionClient";
import ErrorScreen from "../errors/ErrorScreen";
import OrderDetailsNewVersion from "../components/customerOrderDetail/orderDetailsNewVersion/OrderDetailsNewVersion";
import AccessDeniedAsNscNotOnboarded from "../components/accessDenied/AccessDeniedAsNscNotOnboarded";
import Nsc from "./pure/Nsc";
import NscOnboardingClient from "../shared/clients/NscOnboardingClient";

function DealerApp(props) {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] =
    useState(false);
  const [dealerGroup, setDealerGroup] = useState([]);
  const [dealerGroupFiltered, setDealerGroupFiltered] = useState([]);
  const [padding, setPadding] = useState("70px");

  const localizedInfo = useRef({});
  const lang = useRef(undefined);

  const [currentDealer, setCurrentDealer] = useState(
    new Dealer(props.userDetails, props.history)
  );
  const user = useRef(null);

  // Initialize Dealer and Nsc instances when userDetails or history change
  useEffect(() => {
    if (props.userDetails && props.history) {
      setCurrentDealer(
        new Dealer(props.userDetails, props.history)
      );
      user.current = new Nsc(props.userDetails, props.history);
    }
  }, [props.userDetails, props.history]);

  // Listen for route changes and set navigation restriction
  useEffect(() => {
    const unlisten = props.history.listen(() => setNavigationRestriction());
    return () => {
      if (unlisten) unlisten();
    };
  }, [props.history]);

  // Run getLocalizedInfo on mount (componentDidMount equivalent)
  useEffect(() => {
    getLocalizedInfo();
  }, []);

  // Move updateDealer above dealerChanged to fix temporal dead zone error
  const updateDealer = (dealer, showAll = false, shouldGetPendingActions = false) => {
    const newDealer = currentDealer.updateCurrentDealer(dealer, showAll);

    const hasPendingActionPermission =
      currentDealer.permissions.rolePermissions.hasOwnProperty([
        "pendingActionQueue:view",
      ]);

    if (shouldGetPendingActions && hasPendingActionPermission) {
      getPendingActions(newDealer, false);
    } else {
      const newDealerForState = Object.create(Object.getPrototypeOf(newDealer));
      Object.assign(newDealerForState, newDealer);

      setCurrentDealer(newDealerForState);
    }
  };

  const dealerChanged = useCallback(
    (dealer, showAll = false) => {
      updateDealer(dealer, showAll, true);
    },
    [updateDealer]
  );

  const navigateToRoute = useCallback(
    (link) => {
      props.history.push(`/${link}`);
    },
    [props.history]
  );

  const onReservationSave = useCallback(() => {
    // Create a new Dealer instance with updated status to ensure React state updates
    const updatedDealer = Object.create(Object.getPrototypeOf(currentDealer));
    Object.assign(updatedDealer, currentDealer, {
      onBoardingStatus: "ACCEPTED",
    });
    setCurrentDealer(updatedDealer);
    window.location.href = "/";
  }, [currentDealer]);

  const onDecline = useCallback(() => {
    const updatedDealer = Object.create(Object.getPrototypeOf(currentDealer));
    Object.assign(updatedDealer, currentDealer, {
      onBoardingStatus: "DECLINED",
    });
    setCurrentDealer(updatedDealer);
    navigateToRoute("declined");
  }, [currentDealer, navigateToRoute]);

  const onDealerDecline = useCallback(() => {
    const updatedDealer = Object.create(Object.getPrototypeOf(currentDealer));
    Object.assign(updatedDealer, currentDealer, {
      onBoardingStatus: "PENDING",
    });
    setCurrentDealer(updatedDealer);
    navigateToRoute("");
  }, [currentDealer, navigateToRoute]);

  const getLocalizedInfo = () => {
    LocalizedInfoClient.getLocalizedInfo(currentDealer)
      .then((response) => {
        localizedInfo.current = Object.assign(
          {},
          { ...localizedInfo.current },
          { ...response }
        );
        lang.current = localizedInfo.current.defaultLanguage.isocode;

        props.i18n.changeLanguage(
          lang.current.substring(0, 2) + "_" + props.userDetails.market
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        hasNscOnboarded();
      });
  };

  const updateDealerGroupDetails = () => {
    hasNscOnboarded();
  };

  const isBevEnabledAndOnboarded = () => {
    try {
      if (typeof props.hideOrShow !== "function") {
        console.error(
          "[DealerApp] ERROR: props.hideOrShow is not a function!",
          props.hideOrShow
        );
      }
      props.hideOrShow(true);
    } catch (err) {
      console.error(
        "[DealerApp] Exception calling props.hideOrShow(true):",
        err,
        err.stack,
        props
      );
    }
    DealerGroupingClient.getDealerGroup(currentDealer)
      .then((data) => {
        let dealer;

        if (data.length > 1) {
          dealer = data.filter(
            (dealer) =>
              dealer.dealerType === "MDLR" || dealer.dealerType === "HUB"
          )[0];
        } else {
          dealer = data[0];
        }

        const bevAndIceDealers = data.sort((a, b) => {
          if (a.dealerType === "MDLR" || a.dealerType === "HUB") {
            return -1;
          } else if (b.dealerType === "MDLR" || b.dealerType === "HUB") {
            return 1;
          } else {
            return 0;
          }
        });

        dealer.dealerType === "MDLR" || dealer.dealerType === "HUB"
          ? (setDealerGroupWithLog(bevAndIceDealers),
            setDealerGroupFiltered(
              bevAndIceDealers.filter((dealer) => dealer.dealerType !== "SDLR")
            ))
          : (setDealerGroupWithLog(bevAndIceDealers),
            setDealerGroupFiltered(bevAndIceDealers));

        currentDealer.setDealerGroup(bevAndIceDealers);
        updateDealer(dealer, true);
        const updatedDealer = Object.create(
          Object.getPrototypeOf(currentDealer)
        );
        Object.assign(updatedDealer, currentDealer, {
          onBoardingStatus: dealer.onBoardingStatus,
          dealerActiveStatus: dealer.dealerActiveStatus,
        });
        setCurrentDealer(updatedDealer);
      })
      .catch(() => {})
      .finally(() => {
        determinePendingActionStatuses();
      });
  };

  const determinePendingActionStatuses = () => {
    PendingActionClient.getHasNscCompletedPA(currentDealer, 1)
      .then((hasNscCompletedBasicPAs) => {
        const dealer = currentDealer;
        dealer.hasNscCompletedBasicPAs = hasNscCompletedBasicPAs;
        // setCurrentDealer(dealer);
      })
      .finally(() => {
        getPendingActions().finally(() => performFinalActions());
      });
  };

  const hasNscOnboarded = () => {
    NscOnboardingClient.isOnboarded(user.current).then((hasNscOnboarded) => {
      if (hasNscOnboarded.onboarded) {
        isBevEnabledAndOnboarded();
      } else {
        const updatedDealer = Object.create(
          Object.getPrototypeOf(currentDealer)
        );
        Object.assign(updatedDealer, currentDealer, {
          onBoardingStatus: "NSC_NOT_ONBOARDED",
        });
        setCurrentDealer(updatedDealer);
        navigateToRoute("nsc-not-onboarded");
        performFinalActions();
      }
    });
  };

  const performFinalActions = () => {
    setNavigationRestriction();
    try {
      if (typeof props.hideOrShow !== "function") {
        console.error(
          "[DealerApp] ERROR: props.hideOrShow is not a function!",
          props.hideOrShow,
          props
        );
      }
      props.hideOrShow(false);
    } catch (err) {
      console.error(
        "[DealerApp] Exception calling props.hideOrShow(false) in performFinalActions:",
        err,
        err.stack,
        props
      );
    }
    setRenderApp(true);
  };

  const setNavigationRestriction = () => {
    if (currentDealer.onBoardingStatus !== "ACCEPTED") {
      setShouldNavigationBeRestricted(true);
    } else {
      const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
        props.history.location.pathname
      );
      setShouldNavigationBeRestricted(!isAllowedPath);
    }
  };

  const isCurrentPathAllowedToShowHeaderAndFooter = (currentPathName) => {
    const paths = [
      "/help",
      "/dealer/customer-reservations",
      "/business-settings",
      "/dashboard",
      "/pricing/deposit",
      "/pricing",
      "/",
    ];

    if (paths.includes(currentPathName)) {
      return true;
    } else {
      const allowedCustomerReservation = matchPath(currentPathName, {
        path: `/dealer/:commonId/customer-reservations/order-details/:id`,
      });
      return !!allowedCustomerReservation;
    }
  };

  const updateDealerPhoneAndEmail = (dealer, email, phone) => {
    const index = dealerGroup.indexOf(
      dealerGroup.filter((d) => d.completeDealerId === dealer.dealerId)[0]
    );

    const dealerGroupCopy = JSON.parse(JSON.stringify(dealerGroup));
    dealerGroupCopy[index].emailAddress = email;
    dealerGroupCopy[index].phoneNumber = phone;

    setDealerGroupWithLog(dealerGroupCopy);
  };

  const updateDealerEmpMembership = (dealer) => {
    const index = dealerGroup.indexOf(
      dealerGroup.filter(
        (d) => d.completeDealerId === dealer.completeDealerId
      )[0]
    );

    const dealerGroupCopy = JSON.parse(JSON.stringify(dealerGroup));
    dealerGroupCopy[index].status = dealer.status;

    setDealerGroupWithLog(dealerGroupCopy);
  };

  const isMainViewingSDLR = (dealer) => {
    // Returns true if a main dealer is currently viewing an SDLR
    return currentDealer.isLoggedInUserMDLR && dealer.dealerType === "SDLR";
  };

  const getPendingActions = async (
    dealer = currentDealer,
    updatePAOnly = true
  ) => {
    console.log("[DealerApp] getPendingActions called");
    try {
      if (typeof props.hideOrShow !== "function") {
        console.error(
          "[DealerApp] ERROR: props.hideOrShow is not a function!",
          props.hideOrShow,
          props
        );
      }
      props.hideOrShow(true);
    } catch (err) {
      console.error(
        "[DealerApp] Exception calling props.hideOrShow(true) in getPendingActions:",
        err,
        err.stack,
        props
      );
    }

    await PendingActionClient.getDealerPendingActionsNotifications(
      dealer,
      false
    )
      .then((data) => {
        try {
          if (typeof props.hideOrShow !== "function") {
            console.error(
              "[DealerApp] ERROR: props.hideOrShow is not a function!",
              props.hideOrShow,
              props
            );
          }
          props.hideOrShow(false);
        } catch (err) {
          console.error(
            "[DealerApp] Exception calling props.hideOrShow(false) in getPendingActions:",
            err,
            err.stack,
            props
          );
        }

        if (updatePAOnly) {
          currentDealer.updatePendingActions(data.pendingActions);
        } else {
          dealer.updatePendingActions(data.pendingActions);
          const newDealerForState = Object.create(Object.getPrototypeOf(dealer));
          Object.assign(newDealerForState, dealer);

          setCurrentDealer(newDealerForState);
        }
      })
      .catch((error) => {
        try {
          if (typeof props.hideOrShow !== "function") {
            console.error(
              "[DealerApp] ERROR: props.hideOrShow is not a function!",
              props.hideOrShow,
              props
            );
          }
          props.hideOrShow(false);
        } catch (err) {
          console.error(
            "[DealerApp] Exception calling props.hideOrShow(false) in getPendingActions catch:",
            err,
            err.stack,
            props
          );
        }

        currentDealer.updatePendingActions([]);
        logMsg("the error is: ", error);
      });
  };

  const ProtectedRoute = ({
    component: Component,
    perform: perform,
    ...rest
  }) => (
    <HasPermissionTo
      perform={perform}
      permissions={currentDealer.permissions.rolePermissions}
      render={() => (
        <Route
          {...rest}
          render={(props) =>
            currentDealer.onBoardingStatus === "ACCEPTED" ? (
              <Component
                {...rest.customprops}
                {...props}
                {...props.match.params}
              />
            ) : (
              <Redirect to="/" />
            )
          }
        />
      )}
    />
  );

  const DeclinedRoute = ({ component: Component, ...rest }) => (
    <Route
      {...rest}
      render={() =>
        currentDealer.onBoardingStatus === "DECLINED" ? (
          <Component {...rest.customprops} {...props} />
        ) : (
          <Redirect to="/" />
        )
      }
    />
  );

  const getProgressBarTabs = useCallback(
    () => ProgressBarTabs().onboardingDealer,
    []
  );

  // Log whenever setDealerGroup is called
  const setDealerGroupWithLog = (group) => {
    console.log("[DealerApp] setDealerGroup called with:", group);
    setDealerGroup(group);
  };

  // App Rendering
  return renderApp ? (
    <Router history={props.history}>
      <div className="appWrapper" id="appWrapper">
        {/*{shouldNavigationBeRestricted ? null : (*/}
          <div style={{ paddingTop: padding }}>
            <GlobalNavBar
              dealerGroup={dealerGroup}
              onDealerChanged={dealerChanged}
              user={currentDealer}
              urlPath={window.location.pathname}
              setPadding={setPadding}
              navigateToRoute={navigateToRoute}
              {...props}
            />
          </div>
        {/*)}*/}
        <Switch>
          <ProtectedRoute
            exact
            path="/dashboard"
            component={withIdleTimer(DealerDashboard)}
            perform={["dashBoardPage"]}
            customprops={{
              user: currentDealer,
              isMainViewingSDLR: isMainViewingSDLR,
              onDealerChanged: dealerChanged,
              lang: lang.current,
              hideOrShow: props.hideOrShow,
            }}
          />
          <ProtectedRoute
            exact
            path="/pricing"
            component={withIdleTimer(PricingWrapper)}
            perform={["pricingPage"]}
            customprops={{
              user: currentDealer,
              isMainViewingSDLR: isMainViewingSDLR,
              localizedInfo: localizedInfo.current,
              onDealerChanged: dealerChanged,
            }}
          />
          <ProtectedRoute
            exact
            path="/dealer/:commonId/customer-reservations/order-details/:reservationId"
            component={withIdleTimer(OrderDetailsNewVersion)}
            perform={["ordersPage"]}
            customprops={{
              user: currentDealer,
              lang: lang.current,
              localizedInfo: localizedInfo.current,
              onDealerChanged: dealerChanged,
              hideOrShow: props.hideOrShow,
            }}
          />
          <ProtectedRoute
            exact
            path="/dealer/customer-reservations"
            component={withIdleTimer(CustomerReservations)}
            perform={["ordersPage"]}
            customprops={{
              user: currentDealer,
              lang: lang.current,
              onDealerChanged: dealerChanged,
              hideOrShow: props.hideOrShow,
            }}
          />
          <ProtectedRoute
            exact
            path="/business-settings"
            component={withIdleTimer(BusinessSettings)}
            perform={["businessSettingsPage"]}
            customprops={{
              isMainViewingSDLR: isMainViewingSDLR,
              getPendingActions: getPendingActions,
              user: currentDealer,
              lang: lang.current,
              dealerGroup: dealerGroup,
              updateDealerGroupDetails: updateDealerGroupDetails,
              localizedInfo: localizedInfo.current,
              updateDealerPhoneAndEmail: updateDealerPhoneAndEmail,
              updateDealerEmpMembership: updateDealerEmpMembership,
              onDealerChanged: dealerChanged,
              permissions: props.userDetails.permissions,
              hideOrShow: props.hideOrShow,
            }}
          />
          <Route
            exact
            path="/help"
            component={withIdleTimer(() => (
              <Help {...props} market={currentDealer.market} />
            ))}
          />
          <DeclinedRoute
            exact
            path="/declined"
            component={withIdleTimer(TermsDeclined)}
            customprops={{
              user: currentDealer,
              onDecline: onDealerDecline,
            }}
          />
          <ProtectedRoute
            exact
            path="/pricing/deposit"
            perform={["depositPricingTab"]}
            component={withIdleTimer(() => (
              <DepositPricing
                localizedInfo={localizedInfo.current}
                user={currentDealer}
                isMainViewingSDLR={isMainViewingSDLR}
                onDealerChanged={dealerChanged}
                getPendingActions={getPendingActions}
                hideOrShow={props.hideOrShow}
                isNsc={currentDealer.isNsc || false}
                t={props.t}
                i18n={props.i18n}
                userDetails={props.userDetails}
                history={props.history}
              />
            ))}
          />

          <Route exact path="/denied" component={AccessDeniedAsBevDisabled} />
          <Route
            exact
            path="/notonboarded"
            component={AccessDeniedAsNotOnBoarded}
          />
          <Route
            exact
            path="/nsc-not-onboarded"
            component={AccessDeniedAsNscNotOnboarded}
          />
          <Route
            exact
            path="/"
            render={() => {
              if (
                currentDealer.onBoardingStatus === "ACCEPTED" ||
                (currentDealer.onBoardingStatus === "DECLINED" &&
                  currentDealer.dealerActiveStatus === "IN_ACTIVE")
              ) {
                return (
                  <Redirect
                    to={{
                      pathname: "/dashboard",
                    }}
                  />
                );
              } else if (currentDealer.onBoardingStatus === "DECLINED") {
                return (
                  <Redirect
                    to={{
                      pathname: "/declined",
                    }}
                  />
                );
              } else if (
                currentDealer.onBoardingStatus === "ONBOARDINGNOTCOMPLETED"
              ) {
                return (
                  <Redirect
                    to={{
                      pathname: "/notonboarded",
                    }}
                  />
                );
              } else if (currentDealer.onBoardingStatus === "PENDING") {
                return (
                  <QuickStart
                    {...props}
                    user={currentDealer}
                    isMainViewingSDLR={isMainViewingSDLR}
                    dealerGroup={dealerGroupFiltered}
                    history={props.history}
                    onboardingClient={DealerOnboardingClient}
                    localizedInfo={localizedInfo.current}
                    onSave={onReservationSave}
                    onDecline={onDecline}
                    progressBarTabs={getProgressBarTabs()}
                    Preview={PreviewTermsConditions}
                    fromQuickstart={true}
                  />
                );
              } else {
                return null;
              }
            }}
          />
          <Route
            exact
            path="/error"
            render={(props) => <ErrorScreen {...props} />}
          />
          <Route component={withIdleTimer(InvalidRoute)} />
        </Switch>
        {shouldNavigationBeRestricted ? null : (
          <Footer permissions={props.userDetails.permissions} />
        )}
      </div>
    </Router>
  ) : null;
}

export default DealerApp;
